import {
  useHtzWorkOrderPutMutation,
  useHtzWorkOrderShowQuery,
  useLazyHtzWorkOrderShowQuery,
} from "@/services/backend/htz/work-order/api/work-order-api";
import { UUID } from "@bitory-gmbh/nidavellir";
import {
  documentDbGet,
  documentDbPut,
} from "@/shared/lib/storage/indexdb/document-db";
import { WorkOrder } from "@/services/backend/htz/work-order/types/work-order";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import t from "@/lang/lang";
import {
  Mutation,
  Query,
  useDocumentDBGet,
  useDocumentDBPut,
} from "@/shared/lib/storage/indexdb/document-db-hooks";
import { useParams } from "react-router";
import { useStaleContext } from "./stale-context";
import { OfflineState, useOfflineContext } from "./offline-context";

/*
 * useWorkOrderOffline prepares two functions. One to
 * prepare the work order for offline usage and one to
 * sync the offline state back to the server.
 *
 * When calling the functions, the offline database must
 * be set up. The caller must catch thrown errors.
 */
export function useWorkOrderOffline(workOrderId: UUID) {
  const { setSynced } = useOfflineContext();
  const { addStale } = useStaleContext();
  const [load] = useLazyHtzWorkOrderShowQuery();
  const [put] = useHtzWorkOrderPutMutation();

  const prepMsg = t("Speichere Arbeitsschein ...");
  const syncMsg = t("Synchronisiere Arbeitsschein ...");
  const doneMsg = t("Arbeitsschein");

  const prepareWorkOrder = async () => {
    // Load the latest state
    const result = await load({ id: workOrderId });
    if (result.error) {
      throw result.error;
    }

    // When going offline, data is set to stale, such that
    // all data is reloaded from the offline database.
    addStale("workOrder");

    // Append to synced state for user information
    setSynced((prev) => [...prev, prepMsg]);

    // Save to index DB
    await documentDbPut<WorkOrder>(
      GSMDocumentKind.WorkOrder,
      workOrderId,
      "workOrder",
      result.data!,
    );

    // Replace in synced state for user information
    setSynced((prev) => prev.map((p) => (p === prepMsg ? doneMsg : p)));
  };

  const syncWorkOrder = async () => {
    // Append to synced state for user information
    setSynced((prev) => [...prev, syncMsg]);

    // Load from index DB
    const idbWorkOrder = await documentDbGet<WorkOrder>(
      GSMDocumentKind.WorkOrder,
      workOrderId,
      "workOrder",
      workOrderId,
    );

    // Sync to server
    await put(idbWorkOrder!).unwrap();

    // Replace in synced state for user information
    setSynced((prev) => prev.map((p) => (p === syncMsg ? doneMsg : p)));
  };

  return { prepareWorkOrder, syncWorkOrder };
}

export function usePutWorkOrder(): Mutation<WorkOrder, WorkOrder> {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const [putWorkOrderMutation, { isLoading, error, isSuccess, reset }] =
    useHtzWorkOrderPutMutation();

  const [
    putToIndexedDB,
    {
      isLoading: idbIsLoading,
      error: idbError,
      isSuccess: idbIsSuccess,
      reset: idbReset,
    },
  ] = useDocumentDBPut<WorkOrder, WorkOrder>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "workOrder",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return [putWorkOrderMutation, { isLoading, error, isSuccess, reset }];
    case OfflineState.OFFLINE:
      return [
        putToIndexedDB,
        {
          isLoading: idbIsLoading,
          error: idbError,
          isSuccess: idbIsSuccess,
          reset: idbReset,
        },
      ];
    default:
      return [
        () => {
          throw new Error(
            `Put of work order not possible is state ${offlineState}.`,
          );
        },
        {
          isLoading: false,
          error: undefined,
          isSuccess: false,
          reset: () => null,
        },
      ];
  }
}

export function useGetWorkOrder(workOrderId: string): Query<WorkOrder> {
  const { offlineState } = useOfflineContext();

  const {
    data: serverData,
    isLoading: serverIsLoading,
    error: serverError,
  } = useHtzWorkOrderShowQuery({ id: workOrderId });

  const {
    data: indexedDBData,
    isLoading: indexedDBIsLoading,
    error: indexedDBError,
  } = useDocumentDBGet<WorkOrder>(
    GSMDocumentKind.WorkOrder,
    workOrderId,
    "workOrder",
    workOrderId,
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: serverData,
        isLoading: serverIsLoading,
        error: serverError,
      };
    case OfflineState.OFFLINE:
      return {
        data: indexedDBData,
        isLoading: indexedDBIsLoading,
        error: indexedDBError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: { status: 500 },
      };
  }
}
